import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface DistributionRow {
  readonly assetDistributionId: string;
  readonly distributionCode: string;
  readonly assetId: string;
  readonly recipientType: string;
  readonly recipientName: string | null;
  readonly distributionChannel: string;
  readonly status: string;
}

const RECIPIENT_TYPES = ['', 'DEPARTMENT', 'VENDOR', 'AGENCY', 'USER', 'CAMPAIGN', 'PARTNER'];
const CREATE_RECIPIENTS = ['DEPARTMENT', 'VENDOR', 'AGENCY', 'USER', 'CAMPAIGN', 'PARTNER'];
const CHANNELS = ['', 'EMAIL', 'DOWNLOAD_LINK', 'PORTAL', 'API', 'SHARE', 'CDN'];
const CREATE_CHANNELS = ['EMAIL', 'DOWNLOAD_LINK', 'PORTAL', 'API', 'SHARE', 'CDN'];
const STATUSES = ['', 'DISTRIBUTED', 'DELIVERED', 'REVOKED', 'EXPIRED', 'FAILED'];

/**
 * M36 — Distribution Dashboard (§31). Register of asset distributions (T486)
 * across the 6 recipient types and 6 delivery channels, tracking the
 * distributed → delivered → revoked/expired lifecycle so every distributed
 * asset remains fully traceable to its recipient (BR-04). Filterable by
 * recipient type, channel and status. Tenant isolation is enforced server-side
 * (LAW 4).
 */
export function DistributionDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DistributionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [recipientType, setRecipientType] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newAssetId, setNewAssetId] = useState('');
  const [newRecipientType, setNewRecipientType] = useState(CREATE_RECIPIENTS[0]);
  const [newRecipientName, setNewRecipientName] = useState('');
  const [newChannel, setNewChannel] = useState(CREATE_CHANNELS[0]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (recipientType) params.set('recipientType', recipientType);
    if (channel) params.set('channel', channel);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: DistributionRow[]; total: number }>(`/v1/dam/distributions${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, recipientType, channel, status, reload]);

  const on403 = (err: unknown): void => {
    if (err instanceof ApiError && err.status === 403) setNotice(t('m36.common.permRequired', { perm: 'M36_DISTRIBUTION_MANAGE' }));
    else setNotice((err as Error).message);
  };

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setNotice(null);
    if (!newAssetId.trim()) { setNotice(t('m36.common.assetIdReq')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/dam/distributions`, {
        method: 'POST',
        body: {
          assetId: newAssetId.trim(),
          recipientType: newRecipientType,
          recipientName: newRecipientName.trim() || undefined,
          distributionChannel: newChannel,
        },
        token,
      });
      setNotice(t('m36.distribution.created', { asset: newAssetId.trim() }));
      setNewAssetId(''); setNewRecipientName('');
      setReload((n) => n + 1);
    } catch (err) {
      on403(err);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (r: DistributionRow): Promise<void> => {
    setNotice(null);
    setBusyId(r.assetDistributionId);
    try {
      await apiRequest(`/v1/dam/distributions/${r.assetDistributionId}/revoke`, { method: 'POST', body: {}, token });
      setNotice(t('m36.distribution.revoked', { code: r.distributionCode }));
      setReload((n) => n + 1);
    } catch (err) {
      on403(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="distribution-dashboard">
      <h1>{t('m36.distribution.title')}</h1>
      <form aria-label="create-distribution-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m36.common.asset')}
          <input aria-label="field-asset" value={newAssetId} onChange={(e) => setNewAssetId(e.target.value)} placeholder={t('m36.common.assetIdPh')} />
        </label>
        <label>
          {t('m36.distribution.recipientType')}
          <select aria-label="field-recipient-type" value={newRecipientType} onChange={(e) => setNewRecipientType(e.target.value)}>
            {CREATE_RECIPIENTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>
          {t('m36.distribution.recipientName')}
          <input aria-label="field-recipient-name" value={newRecipientName} onChange={(e) => setNewRecipientName(e.target.value)} />
        </label>
        <label>
          {t('m36.common.channel')}
          <select aria-label="field-channel" value={newChannel} onChange={(e) => setNewChannel(e.target.value)}>
            {CREATE_CHANNELS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <Button variant="primary" type="submit" aria-label="create-distribution" disabled={busy}>{t('m36.distribution.create')}</Button>
      </form>
      {notice && <p role="status" aria-label="distribution-notice">{notice}</p>}
      <div className="filters">
        <label>
          {t('m36.distribution.recipient')}
          <select aria-label="filter-recipient" value={recipientType} onChange={(e) => setRecipientType(e.target.value)}>
            {RECIPIENT_TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.channel')}
          <select aria-label="filter-channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
            {CHANNELS.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="distribution-count">{t('m36.distribution.count', { count: total })}</p>
          <DataTable
            ariaLabel="distribution-table"
            rows={rows}
            rowKey={(r) => r.assetDistributionId}
            columns={[
              { key: 'distributionCode', header: t('m36.common.code'), render: (r) => r.distributionCode },
              { key: 'assetId', header: t('m36.common.asset'), render: (r) => r.assetId },
              { key: 'recipientType', header: t('m36.distribution.recipientType'), render: (r) => r.recipientType },
              { key: 'recipientName', header: t('m36.distribution.recipient'), render: (r) => r.recipientName ?? '' },
              { key: 'distributionChannel', header: t('m36.common.channel'), render: (r) => r.distributionChannel },
              { key: 'status', header: t('m36.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m36.common.actions'), render: (r) => (
                <>
                    {r.status !== 'REVOKED' && (
                      <Button variant="danger" type="button" aria-label={`revoke-${r.distributionCode}`} disabled={busyId === r.assetDistributionId} onClick={() => void revoke(r)}>{t('m36.distribution.revoke')}</Button>
                    )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
